import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { Alert, Badge, Button, Spinner } from '../../components/ui';
import { Agreement, AgreementVerifyResult, BASE, SignedUrlResponse, statusLabel, statusTone } from './api';
import { VerifyPanel } from './VerifyPanel';

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 text-sm">
      <dt className="text-slate-600">{label}</dt>
      <dd className="col-span-2 break-words">{children}</dd>
    </div>
  );
}

/** Details of one agreement, a PDF download and the Verify button. */
export function AgreementDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ['agreements', 'one', id], queryFn: () => api.get(`${BASE}/${id}`, { schema: Agreement }) });
  const verify = useMutation({ mutationFn: () => api.post(`${BASE}/${id}/verify`, { schema: AgreementVerifyResult }) });
  const download = useMutation({
    mutationFn: async () => {
      const r = await api.get(`${BASE}/${id}/pdf`, { schema: SignedUrlResponse });
      window.open(r.url, '_blank', 'noopener');
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const a = q.data;
  const title = a ? (a.seq === null ? t('agreementsUi.drawer.titlePending') : t('agreementsUi.drawer.title', { seq: a.seq })) : t('agreementsUi.title');
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40">
      <aside role="dialog" aria-modal="true" aria-label={title} className="h-full w-full max-w-lg overflow-y-auto bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" aria-label={t('agreementsUi.drawer.close')} onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-slate-100">
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
        {q.isPending ? <Spinner /> : null}
        {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
        {a ? (
          <div className="space-y-4">
            <dl className="divide-y divide-slate-100">
              <Detail label={t('common.status')}>
                <Badge tone={statusTone(a.status)}>{statusLabel(a.status)}</Badge>
              </Detail>
              <Detail label={t('agreementsUi.detail.person')}>{a.personName ?? t('agreementsUi.redactedName')}</Detail>
              <Detail label={t('agreementsUi.detail.notice')}>{t('agreementsUi.noticeVersion', { title: a.noticeTitle, version: a.noticeVersion })}</Detail>
              <Detail label={t('agreementsUi.detail.site')}>{a.siteName}</Detail>
              <Detail label={t('agreementsUi.detail.group')}>{a.groupName}</Detail>
              <Detail label={t('agreementsUi.detail.device')}>{a.deviceName ?? t('agreementsUi.detail.none')}</Detail>
              <Detail label={t('agreementsUi.detail.accepted')}>{formatDateTime(a.acceptedAt)}</Detail>
              <Detail label={t('agreementsUi.detail.sealed')}>{a.sealedAt ? formatDateTime(a.sealedAt) : t('agreementsUi.detail.none')}</Detail>
              <Detail label={t('agreementsUi.detail.signature')}>{a.hasSignature ? t('common.yes') : t('common.no')}</Detail>
              {a.pdfSha256 ? (
                <Detail label={t('agreementsUi.detail.fingerprint')}>
                  <code className="text-xs">{a.pdfSha256}</code>
                </Detail>
              ) : null}
            </dl>
            {a.errorKey && (a.status === 'failed' || a.status === 'pending') ? <Alert kind="error">{t('agreementsUi.sealError', { reason: t(a.errorKey) })}</Alert> : null}
            {a.status === 'redacted' ? <Alert kind="info">{t('agreementsUi.redactedNote')}</Alert> : null}
            <ErrorLine error={download.error ?? verify.error} />
            <div className="flex flex-wrap gap-2">
              {a.status === 'sealed' ? (
                <Button onClick={() => download.mutate()} disabled={download.isPending}>
                  {t('agreementsUi.download')}
                </Button>
              ) : null}
              {a.seq !== null ? (
                <Button variant="primary" onClick={() => verify.mutate()} disabled={verify.isPending}>
                  {verify.isPending ? t('agreementsUi.verify.running') : t('agreementsUi.verify.button')}
                </Button>
              ) : null}
            </div>
            {verify.data ? <VerifyPanel result={verify.data} /> : null}
          </div>
        ) : null}
      </aside>
    </div>
  );
}
